<script setup lang="ts">
import CompanyNameField from '@/components/companies/CompanyNameField.vue'
import FinestButton from '@/components/FinestButton.vue'
import InertiaFormField from '@/components/InertiaFormField.vue'
import type { PageProps } from '@/types'
import CompaniesController from '@controllers/Investors/CompaniesController'
import { useForm, usePage } from '@inertiajs/vue3'
import { computed, nextTick, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

export type CompanyCreateReturnTarget = 'estate_object_owner' | 'financing_request_borrower'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    returnTo?: CompanyCreateReturnTarget | null
    showTrigger?: boolean
  }>(),
  {
    disabled: false,
    returnTo: null,
    showTrigger: true,
  },
)

const emit = defineEmits<{
  created: [companyId: string]
}>()

const visible = defineModel<boolean>('visible', { default: false })

const { t } = useI18n()

const page = usePage<PageProps<{ flash?: { createdCompanyId?: string | null } }>>()

const form = useForm({ name: '', handelsregister_entity_id: null as string | null })

const nameField = useTemplateRef<{ reset: () => void }>('nameField')
const saveButton = useTemplateRef<{ $el?: HTMLButtonElement }>('saveButton')

const canSubmit = computed(() => !form.processing && form.name.trim().length > 0)

const staysOnCurrentPage = computed(() => props.returnTo !== null)

function focusSaveButton(): void {
  void nextTick(() => globalThis.setTimeout(() => saveButton.value?.$el?.focus()))
}

function submit(): void {
  if (!canSubmit.value) {
    return
  }

  form
    .transform((data) => ({ ...data, ...(props.returnTo ? { return_to: props.returnTo } : {}) }))
    .post(CompaniesController.store().url, {
      preserveScroll: staysOnCurrentPage.value,
      preserveState: staysOnCurrentPage.value,
      onSuccess: () => {
        const createdCompanyId = page.props.flash?.createdCompanyId

        if (createdCompanyId) {
          emit('created', createdCompanyId)
        }

        form.reset()
        visible.value = false
      },
    })
}

function reset(): void {
  form.reset()
  form.clearErrors()
  nameField.value?.reset()
}
</script>

<template>
  <div>
    <FinestButton v-if="showTrigger" :label="t('actions.add')" icon="pi pi-plus" :disabled @click="visible = true" />

    <Dialog v-model:visible="visible" modal :header="t('title')" class="w-full max-w-lg" @after-hide="reset">
      <span class="text-surface-500 mb-6 block">{{ t('subtitle') }}</span>

      <form class="flex flex-col gap-4" @submit.prevent="submit">
        <InertiaFormField :label="t('form.name')" :error="form.errors.name" required>
          <CompanyNameField
            ref="nameField"
            v-model="form.name"
            v-model:handelsregister-entity-id="form.handelsregister_entity_id"
            :invalid="!!form.errors.name"
            @suggestion-picked="focusSaveButton" />
        </InertiaFormField>
      </form>

      <template #footer>
        <FinestButton
          :label="t('actions.cancel')"
          variant="ghost"
          :disabled="form.processing"
          @click="visible = false" />
        <FinestButton
          ref="saveButton"
          :label="t('actions.save')"
          :icon="form.processing ? 'pi pi-spin pi-spinner' : undefined"
          :disabled="!canSubmit"
          @click="submit" />
      </template>
    </Dialog>
  </div>
</template>

<i18n>
{
  "de": {
    "title": "Unternehmen hinzufügen",
    "subtitle": "Gib zunächst nur den Namen an. Weitere Details kannst Du später ergänzen.",
    "actions": {
      "add": "Unternehmen hinzufügen",
      "cancel": "Abbrechen",
      "save": "Speichern"
    },
    "form": {
      "name": "Name"
    }
  },
  "en": {
    "title": "Add company",
    "subtitle": "First, just provide the company name. You can add further details afterwards.",
    "actions": {
      "add": "Add Company",
      "cancel": "Cancel",
      "save": "Save"
    },
    "form": {
      "name": "Name"
    }
  }
}
</i18n>
